import Tool, { type ToolArgs } from '../../../tools/Tool';
import TableUtils from '../TableUtils';
import { Editor, Element, type Path, Transforms } from 'slate';
import { getTableStructureFromSelection } from '../TableStructure';
import type AhaEditorV2Controller from '../../../controllers/AhaEditorV2Controller';
import { isTableCellElement } from '../../../types/guards';

const setVerticalAlign = (
  editor: Editor,
  controller: AhaEditorV2Controller,
  verticalAlign: string
) => {
  if (!editor.selection) return;

  const tableStructure = getTableStructureFromSelection(editor);
  if (!tableStructure) return;

  const tablePath = TableUtils.currentTablePath(editor);
  if (!tablePath) return;

  const { tableSelection } = controller.state.pluginState;

  if (tableSelection?.multipleCellsSelected) {
    tableSelection.selectedCellPaths.forEach((cellPath: Path) => {
      const coords = tableStructure.pathToCoordinates(cellPath);
      if (!coords) return;

      const cellInfo = tableStructure.cell(coords.columnIndex, coords.rowIndex);
      if (cellInfo?.isActual) {
        const cellPath = [...tablePath, ...cellInfo.relativePath];

        Transforms.setNodes(
          editor,
          {
            table_cellVerticalAlign: verticalAlign,
          },
          {
            at: cellPath,
          }
        );
      }
    });
  } else {
    const cellPath = TableUtils.currentCellPath(editor);
    if (!cellPath) return;

    Transforms.setNodes(
      editor,
      {
        table_cellVerticalAlign: verticalAlign,
      },
      {
        at: cellPath,
      }
    );
  }
};

const currentVerticalAlignment = (editor: Editor): string | undefined => {
  if (!editor.selection) return;

  const cellEntry = Editor.above(editor, {
    mode: 'lowest',
    match: (n) => Element.isElement(n) && isTableCellElement(n),
  });
  if (!cellEntry) return;

  return cellEntry[0].table_cellVerticalAlign;
};

class ToolCellVerticalAlignTop extends Tool {
  static toolName = 'Top';
  static iconClass = 'fa-regular fa-arrow-up-to-line';

  static action({ editor, controller }: ToolArgs): void {
    setVerticalAlign(editor, controller, 'top');
  }

  static isActive({ editor }: ToolArgs): boolean {
    return currentVerticalAlignment(editor) === 'top';
  }
}

class ToolCellVerticalAlignMiddle extends Tool {
  static toolName = 'Middle';
  static iconClass = 'fa-regular fa-arrows-to-line';

  static action({ editor, controller }: ToolArgs): void {
    setVerticalAlign(editor, controller, 'middle');
  }

  static isActive({ editor }: ToolArgs): boolean {
    return currentVerticalAlignment(editor) === 'middle';
  }
}

class ToolCellVerticalAlignBottom extends Tool {
  static toolName = 'Bottom';
  static iconClass = 'fa-regular fa-arrow-down-to-line';

  static action({ editor, controller }: ToolArgs): void {
    setVerticalAlign(editor, controller, 'bottom');
  }

  static isActive({ editor }: ToolArgs): boolean {
    return currentVerticalAlignment(editor) === 'bottom';
  }
}

export {
  ToolCellVerticalAlignTop,
  ToolCellVerticalAlignMiddle,
  ToolCellVerticalAlignBottom,
};
